// Personal chat history for the signed-in user.
// Shows all conversations this user has had with any chatbot in their org.
// Backed by GET /api/v1/my/history (MyConversationsController.AllHistory),
// which filters strictly to RequesterUserId == currentUserId + org-scoped bots.
// Accessible to any authenticated user with conversations.view — no
// organization.history plan feature required.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageShell } from "@/components/page-shell";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquare, Bot, FileText, Clock } from "lucide-react";
import { aiSourceLabel, type AiExecutionMetadata } from "@/lib/ai-execution-metadata";

interface MyThread {
  id: string;
  sessionId: string;
  title: string;
  messageCount: number;
  createdAt: string;
  lastActivityAt: string;
  chatbotId: string;
  chatbotName: string;
}

interface MyMessage extends AiExecutionMetadata {
  id: string;
  role: string;
  content: string;
  createdAt: string;
}

function TranscriptDialog({
  thread,
  onClose,
}: {
  thread: MyThread;
  onClose: () => void;
}) {
  const { data: messages, isLoading } = useQuery<MyMessage[]>({
    queryKey: ["/api/v1/my/conversations", thread.id, "messages"],
    queryFn: async () => {
      const res = await fetch(`/api/v1/my/conversations/${thread.id}/messages`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
        },
      });
      if (!res.ok) throw new Error("Failed to load messages");
      return res.json();
    },
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid="dialog-my-transcript"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {thread.title}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {thread.chatbotName} ·{" "}
            {new Date(thread.createdAt).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </p>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : !messages || messages.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No messages in this conversation.
          </p>
        ) : (
          <div className="space-y-2 pt-2">
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              return (
                <div
                  key={m.id}
                  className={`p-3 rounded-md text-sm ${
                    isUser
                      ? "bg-primary/10 ml-8"
                      : "bg-muted/50 mr-8"
                  }`}
                  data-testid={`transcript-message-${i}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-medium">
                      {isUser ? "You" : thread.chatbotName || "Bot"}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(m.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  {m.role === "assistant" && aiSourceLabel(m) && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      <p>AI source: {aiSourceLabel(m)}</p>
                      {m.aiProvider && <p>Provider: {m.aiProvider} · Model: {m.aiModel || "Provider default"}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MyHistoryList() {
  const [selected, setSelected] = useState<MyThread | null>(null);

  const { data: threads, isLoading } = useQuery<MyThread[]>({
    queryKey: ["/api/v1/my/history"],
  });

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="state-my-history-loading">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-5 w-56 mb-2" />
              <Skeleton className="h-4 w-36" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!threads || threads.length === 0) {
    return (
      <Card>
        <CardContent
          className="flex flex-col items-center justify-center py-16"
          data-testid="state-my-history-empty"
        >
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <MessageSquare className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-semibold mb-1">No conversations yet</h2>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            Conversations you have with chatbots will appear here. Head to the
            portal and start a chat to see your history.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-2" data-testid="list-my-history">
        {threads.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelected(t)}
            className="w-full text-left flex items-center justify-between gap-3 p-3 rounded-md bg-muted/40 hover:bg-muted/70 transition-colors"
            data-testid={`my-history-thread-${t.id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-md bg-background border flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <p
                  className="text-sm font-medium truncate"
                  data-testid={`text-my-thread-title-${t.id}`}
                >
                  {t.title}
                </p>
                <p className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
                  <span>{t.chatbotName}</span>
                  <span>·</span>
                  <Clock className="w-3 h-3" />
                  <span>
                    {new Date(t.lastActivityAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="text-[10px] flex-shrink-0">
              {t.messageCount} msg{t.messageCount !== 1 ? "s" : ""}
            </Badge>
          </button>
        ))}
      </div>

      {selected && (
        <TranscriptDialog thread={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

export default function MyHistoryPage() {
  return (
    <PageShell
      title="My History"
      titleTestId="text-my-history-title"
      subtitle="Your personal conversation history across all chatbots."
    >
      <MyHistoryList />
    </PageShell>
  );
}
